import { pageWidth } from '../functions/windowSizeFunctions';

const MOBILE_BREAKPOINT = 1280;
const DESKTOP_MEDIA_QUERY = `(width >= ${MOBILE_BREAKPOINT}px)`;
const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default class Navigation {
    constructor() {
        this.menuToggle = document.querySelector('.jsMenuToggle');
        this.mainNav = document.querySelector('.jsMainNav');
        this.megamenuParents = [
            ...document.querySelectorAll('.jsMegamenuParent'),
        ];
        this.megamenuLists = [...document.querySelectorAll('.jsMegamenuList')];
        this.megamenuBackBtns = [...document.querySelectorAll('.jsButtonBack')];
        this.mainNavOpened = false;
        this.megamenuListActive = null;

        this.searchBarActive = false;
        this.searchBarToggle = document.querySelector('.jsSearchBarBtn');
        this.searchBarWrap = document.querySelector('.jsSearchBarWrap');
        this.searchBarClose = document.querySelector('.jsSearchBarClose');
        this.searchBarInputActions = document.querySelector(
            '.jsSearchBarFormInputActions',
        );

        this.boundTrapFocus = this.trapFocus.bind(this);

        this.desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);

        this.syncMegamenuParentsExpanded();
        this.events();
    }

    events() {
        if (this.menuToggle && this.mainNav) {
            this.menuToggle.addEventListener(
                'click',
                this.toggleMainMenu.bind(this),
            );

            this.mainNav.addEventListener('click', this.closeMenu.bind(this));
        }

        if (
            this.megamenuParents.length &&
            this.megamenuLists.length &&
            this.megamenuBackBtns.length
        ) {
            this.megamenuParents.forEach((parent) =>
                parent.addEventListener(
                    'click',
                    this.showMegamenuList.bind(this),
                ),
            );

            this.megamenuBackBtns.forEach((btn) =>
                btn.addEventListener(
                    'click',
                    this.closeMenuMegamenuList.bind(this),
                ),
            );
        }

        if (this.searchBarToggle && this.searchBarWrap) {
            this.searchBarToggle.addEventListener(
                'click',
                this.toggleSearchBarDisplay.bind(this),
            );
        }

        if (this.searchBarClose && this.searchBarWrap) {
            this.searchBarClose.addEventListener(
                'click',
                this.toggleSearchBarDisplay.bind(this),
            );
        }

        document.addEventListener('keydown', this.handleKeyDown.bind(this));

        this.desktopMediaQuery.addEventListener(
            'change',
            this.syncMegamenuParentsExpanded.bind(this),
        );
    }

    // Mega-menu parents toggle their list only in the mobile drill-down;
    // on desktop the list is revealed by hover/focus, so no aria-expanded there.
    syncMegamenuParentsExpanded() {
        this.megamenuParents.forEach((parent) => {
            if (this.desktopMediaQuery.matches) {
                parent.removeAttribute('aria-expanded');
                return;
            }

            const list = this.megamenuLists.find(
                (item) => item.dataset.listSlug === parent.dataset.parentSlug,
            );

            parent.setAttribute(
                'aria-expanded',
                list && list.classList.contains('is-active') ? 'true' : 'false',
            );
        });
    }

    setMegamenuParentExpanded(parent, expanded) {
        if (!parent || this.desktopMediaQuery.matches) return;

        parent.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    toggleMainMenu() {
        if (this.mainNavOpened) {
            this.menuToggle.classList.remove('is-open');
            this.menuToggle.setAttribute('aria-expanded', 'false');
            this.mainNav.classList.remove('is-open');

            if (this.megamenuLists.length) {
                this.megamenuLists.forEach((list) => {
                    list.classList.remove('is-active');
                });
            }

            this.megamenuParents.forEach((p) =>
                this.setMegamenuParentExpanded(p, false),
            );
            this.megamenuListActive = null;

            if (pageWidth() < MOBILE_BREAKPOINT) {
                this.mainNav.removeEventListener(
                    'keydown',
                    this.boundTrapFocus,
                );
                this.menuToggle.focus();
            }
        } else {
            this.menuToggle.classList.add('is-open');
            this.menuToggle.setAttribute('aria-expanded', 'true');
            this.mainNav.classList.add('is-open');

            if (pageWidth() < MOBILE_BREAKPOINT) {
                this.mainNav.addEventListener('keydown', this.boundTrapFocus);
                const firstFocusable =
                    this.mainNav.querySelector(FOCUSABLE_SELECTOR);
                if (firstFocusable) firstFocusable.focus();
            }
        }

        this.mainNavOpened = !this.mainNavOpened;
    }

    closeMenu(ev) {
        const clickedElement = ev.target;

        if (!clickedElement.closest('.jsNavMenu')) {
            this.toggleMainMenu();
        }
    }

    showMegamenuList(ev) {
        const currentPageWidth = pageWidth();

        if (currentPageWidth < MOBILE_BREAKPOINT) ev.preventDefault();

        const clickedElement = ev.currentTarget;
        const selectedListName = clickedElement.dataset.parentSlug;

        const selectedList = this.megamenuLists.filter(
            (list) => list.dataset.listSlug === selectedListName,
        );

        if (!selectedList.length) return;

        selectedList[0].classList.add('is-active');
        this.setMegamenuParentExpanded(clickedElement, true);
        this.megamenuListActive = selectedListName;
    }

    closeMenuMegamenuList(ev) {
        const clickedElement = ev.currentTarget;
        const selectedListName = clickedElement.dataset.listSlug;

        if (this.megamenuListActive !== selectedListName) return;

        const selectedList = this.megamenuLists.filter(
            (list) => list.dataset.listSlug === selectedListName,
        );

        if (!selectedList.length) return;

        selectedList[0].classList.remove('is-active');

        const parentLink = this.megamenuParents.find(
            (p) => p.dataset.parentSlug === selectedListName,
        );
        if (parentLink) {
            this.setMegamenuParentExpanded(parentLink, false);
            parentLink.focus();
        }

        this.megamenuListActive = null;
    }

    toggleSearchBarDisplay() {
        if (this.searchBarActive) {
            this.searchBarToggle.setAttribute('aria-expanded', 'false');
            this.searchBarWrap.classList.remove('is-active');

            if (this.searchBarInputActions) {
                this.searchBarInputActions.blur();
                this.searchBarInputActions.value = '';
            }

            this.searchBarActive = false;
        } else {
            this.searchBarToggle.setAttribute('aria-expanded', 'true');
            this.searchBarWrap.classList.add('is-active');

            if (this.searchBarInputActions) this.searchBarInputActions.focus();

            this.searchBarActive = true;
        }
    }

    handleKeyDown(ev) {
        if (ev.key !== 'Escape') return;

        if (this.searchBarActive) {
            this.toggleSearchBarDisplay();
            this.searchBarToggle.focus();
            return;
        }

        if (this.megamenuListActive) {
            const activeList = this.megamenuLists.find(
                (list) => list.dataset.listSlug === this.megamenuListActive,
            );
            const parentLink = this.megamenuParents.find(
                (p) => p.dataset.parentSlug === this.megamenuListActive,
            );

            if (activeList) {
                activeList.classList.remove('is-active');
            }
            if (parentLink) {
                this.setMegamenuParentExpanded(parentLink, false);
                parentLink.focus();
            }

            this.megamenuListActive = null;
            return;
        }

        if (this.mainNavOpened) {
            this.toggleMainMenu();
        }
    }

    trapFocus(ev) {
        if (ev.key !== 'Tab') return;

        const focusable = [
            ...this.mainNav.querySelectorAll(FOCUSABLE_SELECTOR),
        ].filter(
            (el) =>
                el.offsetParent !== null &&
                getComputedStyle(el).visibility !== 'hidden',
        );

        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (ev.shiftKey && document.activeElement === first) {
            ev.preventDefault();
            last.focus();
        } else if (!ev.shiftKey && document.activeElement === last) {
            ev.preventDefault();
            first.focus();
        }
    }
}
